import type { AmbienceId } from '../tracks/types';

/**
 * Original procedural audio. Nothing is fetched.
 * Playback starts only after a user gesture unlocks the AudioContext.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private engine: { osc: OscillatorNode; gain: GainNode; filter: BiquadFilterNode } | null = null;
  private noiseGain: GainNode | null = null;
  private volume = 0.7;
  private ambienceKind: AmbienceId = 'none';
  private ambienceStop: (() => void) | null = null;
  private ambienceClock = 0;

  get unlocked(): boolean {
    return this.ctx != null;
  }

  setVolume(volume: number): void {
    this.volume = volume;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(volume * 0.45, this.ctx.currentTime, 0.05);
    }
  }

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const master = ctx.createGain();
    master.gain.value = this.volume * 0.45;
    master.connect(ctx.destination);

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = 52;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 420;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(master);
    osc.start();

    const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 900;
    band.Q.value = 0.7;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0;
    noise.connect(band);
    band.connect(noiseGain);
    noiseGain.connect(master);
    noise.start();

    this.ctx = ctx;
    this.master = master;
    this.engine = { osc, gain, filter };
    this.noiseGain = noiseGain;
    this.syncAmbience();
  }

  setAmbience(kind: AmbienceId): void {
    this.ambienceKind = kind;
    this.ambienceClock = 0;
    this.syncAmbience();
  }

  tickAmbience(dt: number): void {
    if (!this.ctx || !this.master || this.ambienceKind === 'none') return;
    this.ambienceClock += dt;
    if (this.ambienceKind === 'sewer' && this.ambienceClock > 0.85) {
      this.ambienceClock = 0;
      this.drip();
    } else if (this.ambienceKind === 'base' && this.ambienceClock > 1.15) {
      this.ambienceClock = 0;
      this.beep(740, 0.08, 0.05);
    }
  }

  updateDrive(speed: number, slip: number, surfaceDamp = 1): void {
    if (!this.ctx || !this.engine || !this.noiseGain) return;
    const s = Math.abs(speed);
    const now = this.ctx.currentTime;
    this.engine.osc.frequency.setTargetAtTime(46 + s * 6.5, now, 0.08);
    this.engine.filter.frequency.setTargetAtTime(280 + s * 38, now, 0.08);
    this.engine.gain.gain.setTargetAtTime(Math.min(0.2, 0.02 + s * 0.01) * surfaceDamp, now, 0.08);
    this.noiseGain.gain.setTargetAtTime(Math.min(0.16, slip * 0.035), now, 0.06);
  }

  silence(): void {
    this.updateDrive(0, 0);
  }

  private syncAmbience(): void {
    this.ambienceStop?.();
    this.ambienceStop = null;
    if (!this.ctx || !this.master || this.ambienceKind === 'none') return;
    const ctx = this.ctx;
    const master = this.master;
    const kind = this.ambienceKind;
    const gain = ctx.createGain();
    gain.gain.value = kind === 'space' ? 0.03 : kind === 'base' ? 0.04 : 0.055;
    gain.connect(master);
    const nodes: { stop?: () => void }[] = [];

    if (kind === 'desert' || kind === 'snow' || kind === 'dirt' || kind === 'sewer') {
      const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = kind === 'snow' ? 'highpass' : 'lowpass';
      filter.frequency.value = kind === 'snow' ? 700 : kind === 'sewer' ? 240 : 480;
      source.connect(filter);
      filter.connect(gain);
      source.start();
      nodes.push(source);
    }

    if (kind === 'base' || kind === 'sewer' || kind === 'space') {
      const osc = ctx.createOscillator();
      osc.type = kind === 'space' ? 'sine' : 'triangle';
      osc.frequency.value = kind === 'space' ? 96 : kind === 'base' ? 62 : 48;
      const oscGain = ctx.createGain();
      oscGain.gain.value = kind === 'space' ? 0.4 : 0.25;
      osc.connect(oscGain);
      oscGain.connect(gain);
      osc.start();
      nodes.push(osc);
    }

    this.ambienceStop = () => {
      gain.disconnect();
      for (const node of nodes) node.stop?.();
    };
  }

  private drip(): void {
    if (!this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1400, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.05, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.12);
  }

  beep(frequency: number, duration = 0.12, gainValue = 0.12): void {
    if (!this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(gainValue, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + duration + 0.02);
  }

  collision(): void {
    if (!this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(180, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.18, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.22);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.24);
  }
}
